import {
	ErrorModal,
	HelmetDexteel,
	MesfModal,
	useUserContext,
} from "@dexteel/mesf-core";
import { Grid2, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import {
	PROFILE_ITEM_INITIAL_VALUES,
	PROFILE_ITEM_MODAL_MODE,
	type ProfileItem,
	type ProfileItemFormValues,
	type ProfileItemModalMode,
} from "../../../models/ProfileItem";
import {
	deleteProfileItem,
	getProfileCategories,
	upsertProfileItem,
} from "../../../repositories/ProfileItemRepository";

interface UpsertDeleteModalProps {
	dimension: ProfileItem | null;
	modal: ProfileItemModalMode;
	onHide: (shouldUpdate: boolean) => void;
}

const getFormValues = (dimension: ProfileItem | null): ProfileItemFormValues =>
	dimension
		? {
				ProfileItemId: dimension.ProfileItemId,
				ProfileCategoryId: dimension.ProfileCategoryId,
				Code: dimension.Code,
				Description: dimension.Name,
				ValueTypeCode: dimension.ValueTypeCode,
			}
		: PROFILE_ITEM_INITIAL_VALUES;

export const UpsertDeleteModal = ({
	dimension,
	modal,
	onHide,
}: UpsertDeleteModalProps) => {
	const [error, setError] = useState("");
	const queryClient = useQueryClient();
	const { control, handleSubmit, reset } = useForm<ProfileItemFormValues>({
		defaultValues: PROFILE_ITEM_INITIAL_VALUES,
	});

	const {
		state: { defaultAreaId },
	} = useUserContext();
	const { data: categories } = useQuery({
		queryKey: ["profileCategories", defaultAreaId],
		queryFn: ({ signal }) => getProfileCategories(defaultAreaId, signal),
		enabled: modal !== PROFILE_ITEM_MODAL_MODE.CLOSED,
		onError: (queryError: Error) => setError(queryError.message),
	});

	const closeAfterSuccess = async () => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: ["sectionDimensions"] }),
			queryClient.invalidateQueries({ queryKey: ["section-spec"] }),
			queryClient.invalidateQueries({ queryKey: ["section-spec-version"] }),
		]);
		onHide(true);
	};

	const upsertMutation = useMutation(upsertProfileItem, {
		meta: { successMessage: "The section dimension was saved successfully" },
		onSuccess: closeAfterSuccess,
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const deleteMutation = useMutation(deleteProfileItem, {
		meta: { successMessage: "The section dimension was deleted successfully" },
		onSuccess: closeAfterSuccess,
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	useEffect(() => {
		if (modal !== PROFILE_ITEM_MODAL_MODE.CLOSED) {
			reset(
				modal === PROFILE_ITEM_MODAL_MODE.CREATE
					? PROFILE_ITEM_INITIAL_VALUES
					: getFormValues(dimension),
			);
		}
	}, [dimension, modal, reset]);

	const onSubmit: SubmitHandler<ProfileItemFormValues> = (data) => {
		if (modal === PROFILE_ITEM_MODAL_MODE.DELETE) {
			if (data.ProfileItemId !== null)
				deleteMutation.mutate(data.ProfileItemId);
			return;
		}

		upsertMutation.mutate({
			...data,
			ProfileCategoryId:
				data.ProfileCategoryId ?? categories?.[0]?.ProfileCategoryId ?? null,
		});
	};

	const isDelete = modal === PROFILE_ITEM_MODAL_MODE.DELETE;
	const isSubmitting = upsertMutation.isLoading || deleteMutation.isLoading;
	const actionTitle =
		modal === PROFILE_ITEM_MODAL_MODE.CREATE
			? "CREATE"
			: modal === PROFILE_ITEM_MODAL_MODE.UPDATE
				? "EDIT"
				: "DELETE";

	return (
		<>
			{modal !== PROFILE_ITEM_MODAL_MODE.CLOSED && (
				<HelmetDexteel
					title={`${actionTitle} Section Dimension - Section Dimensions`}
				/>
			)}
			<MesfModal
				id="section-dimension-modal"
				title={`${actionTitle} SECTION DIMENSION`}
				open={modal !== PROFILE_ITEM_MODAL_MODE.CLOSED}
				handleClose={() => onHide(false)}
				maxWidth="sm"
			>
				<form
					data-test-id="section-dimension-form"
					onSubmit={handleSubmit(onSubmit)}
				>
					<MesfModal.Content dividers style={{ padding: "10px 30px" }}>
						{isDelete && (
							<Typography sx={{ mb: 2 }}>
								This dimension is used by{" "}
								<strong>{dimension?.ReferenceCount ?? 0}</strong> section
								specifications. Deleting it will retire it from the catalog,
								create replacement subrevisions without it for any affected
								current or scheduled specifications, and remove it from existing
								drafts. Do you want to continue?
							</Typography>
						)}
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="Code"
									control={control}
									rules={{
										required: "Name is required",
										maxLength: {
											value: 10,
											message: "Name cannot exceed 10 characters",
										},
									}}
									render={({ field, fieldState: { error: fieldError } }) => (
										<TextField
											{...field}
											required
											label="Name"
											slotProps={{
												htmlInput: { "data-test-id": "dimension-code-input" },
											}}
											fullWidth
											margin="dense"
											autoComplete="off"
											disabled={isDelete}
											error={Boolean(fieldError)}
											helperText={fieldError?.message}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="Description"
									control={control}
									rules={{
										required: "Description is required",
										maxLength: {
											value: 80,
											message: "Description cannot exceed 80 characters",
										},
										validate: (value) =>
											value.trim().length > 0 || "Description is required",
									}}
									render={({ field, fieldState: { error: fieldError } }) => (
										<TextField
											{...field}
											label="Description"
											fullWidth
											margin="dense"
											autoComplete="off"
											disabled={isDelete}
											multiline
											minRows={2}
											maxRows={4}
											error={Boolean(fieldError)}
											helperText={fieldError?.message}
											slotProps={{
												htmlInput: {
													maxLength: 80,
													"data-test-id": "dimension-description-input",
												},
											}}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={isSubmitting}
							onHide={onHide}
							buttonLabel={isDelete ? "Delete" : "Save"}
							buttonColor={isDelete ? "secondary" : "primary"}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error In Section Dimension"
			/>
		</>
	);
};
